import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { AlertCircle, KeyRound, Loader2, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { config } from '@/config';
import { CeremonyDismissed, CeremonyUnsupported, createCredential, isWebAuthnAvailable } from '@/lib/webauthn';

interface Credential {
  id: string;
  name: string;
  kind: 'passkey' | 'security_key';
  createdAt: string;
  lastUsedAt: string | null;
}

/**
 * Recovery codes come back **once**, from the first credential of *either* kind.
 *
 * ⚠ **Not security keys only, which is what this said while a passkey gated nothing.** Any
 * credential now gates a password sign-in, so a passkey raises a challenge a code can answer —
 * and a passkey-only user with no codes has no way back in at an origin that can run no
 * ceremony, or at all if the device is lost (`docs/WebAuthn.md` §4.1, §6).
 */
interface EnrollResult {
  recoveryCodes?: string[];
}

/** ⚠ The list endpoint wraps the array; reading it as a bare array renders an empty list. */
interface CredentialsList {
  credentials: Credential[];
  recoveryCodesRemaining: number;
}

const api = `${config.backendApi}/api/auth/webauthn`;

/**
 * Enrolling and managing security keys and passkeys (`docs/WebAuthn.md` §1, §5, §6).
 *
 * The two kinds are offered separately because they are not interchangeable. A **passkey** is
 * UV-required — possession plus a PIN or biometric — so it is complete authentication and can
 * replace the password. A **security key** proves possession only, so it is a second factor
 * presented after one. The crate enforces the difference; this is where the user chooses.
 */
export function SecurityKeys() {
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | undefined>(undefined);
  const [codes, setCodes] = useState<string[] | undefined>(undefined);
  const [name, setName] = useState('');
  // Which kind the open dialog is enrolling; `undefined` means closed. Holding the kind here
  // rather than in two booleans keeps the copy and the mutation reading from one value.
  const [adding, setAdding] = useState<'passkey' | 'security_key' | undefined>(undefined);

  const { data, isLoading } = useQuery({
    queryKey: ['webauthnCredentials'],
    queryFn: async () =>
      (await axios.get<CredentialsList>(`${api}/credentials`, { withCredentials: true })).data,
  });
  const credentials = data?.credentials;
  const codesRemaining = data?.recoveryCodesRemaining ?? 0;

  const enroll = useMutation({
    mutationFn: async (kind: 'passkey' | 'security_key') => {
      const { data: start } = await axios.post<{ challengeId: string; challenge: { publicKey: Record<string, unknown> } }>(
        `${api}/register/start`,
        { kind },
        { withCredentials: true },
      );
      const credential = await createCredential(start.challenge);
      const { data } = await axios.post<EnrollResult>(
        `${api}/register/finish`,
        { challengeId: start.challengeId, credential, name: name.trim() || undefined },
        { withCredentials: true },
      );
      return data;
    },
    onSuccess: (data) => {
      setName('');
      setError(undefined);
      setAdding(undefined);
      // Present only on the *first* enrollment. A later key does not re-mint the batch, because
      // silently replacing it would invalidate codes the user has already written down.
      if (data.recoveryCodes?.length) setCodes(data.recoveryCodes);
      void queryClient.invalidateQueries({ queryKey: ['webauthnCredentials'] });
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: (e: unknown) => {
      if (e instanceof CeremonyUnsupported) console.error('WebAuthn enrollment refused', e.cause);
      setError(
        e instanceof CeremonyDismissed
          ? 'That prompt was dismissed. Nothing was enrolled.'
          : e instanceof CeremonyUnsupported
            ? `${e.message} Try a different browser or key.`
            : 'That key could not be enrolled. It may already be registered to this account.',
      );
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => axios.delete(`${api}/credentials/${id}`, { withCredentials: true }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['webauthnCredentials'] });
      void queryClient.invalidateQueries({ queryKey: ['currentUser'] });
    },
    onError: () => { setError('That key could not be removed.'); },
  });

  if (!isWebAuthnAvailable()) {
    return (
      <section className="space-y-2">
        <h2 className="text-lg font-semibold">Security keys</h2>
        <p className="text-sm text-muted-foreground">
          This browser does not support security keys.
        </p>
      </section>
    );
  }

  return (
    <section className="space-y-4" data-testid="security-keys">
      <div className="space-y-1">
        <h2 className="text-lg font-semibold">Security keys and passkeys</h2>
        <p className="text-sm text-muted-foreground">
          A passkey signs you in on its own. A security key is a second step after your password.
        </p>
      </div>

      {/* Only when no dialog is open. Radix marks background content aria-hidden while a modal
          is up, so an error rendered here during enrollment is invisible — to a screen reader
          and to anyone looking at the screen. The dialog carries its own copy below. */}
      {error && adding === undefined && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {codes && (
        // ⚠ Shown once and never again — the server keeps only hashes.
        <Alert data-testid="recovery-codes">
          <AlertDescription className="space-y-2">
            <p className="font-medium">Save these recovery codes now. They will not be shown again.</p>
            <ul className="grid grid-cols-2 gap-1 font-mono text-xs">
              {codes.map((c) => <li key={c}>{c}</li>)}
            </ul>
            <Button size="sm" variant="outline" onClick={() => { setCodes(undefined); }}>
              I have saved them
            </Button>
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : credentials?.length ? (
        <ul className="divide-y rounded-md border" data-testid="credential-list">
          {credentials.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 p-3">
              <div className="min-w-0">
                <p className="truncate text-sm font-medium">{c.name}</p>
                <p className="text-xs text-muted-foreground">
                  {c.kind === 'passkey' ? 'Passkey' : 'Security key'}
                  {c.lastUsedAt ? ` · last used ${new Date(c.lastUsedAt).toLocaleDateString()}` : ' · never used'}
                </p>
              </div>
              <Button
                size="sm"
                variant="ghost"
                aria-label={`Remove ${c.name}`}
                disabled={remove.isPending}
                onClick={() => { setError(undefined); remove.mutate(c.id); }}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">No keys enrolled yet.</p>
      )}

      {/* ⚠ **Any credential, not security keys only.** Gating this on `security_key` hid the
          count from passkey-only accounts — who are minted codes just the same, and so were
          shown the batch once with nothing afterwards to say any remained, or that they had
          run out. `length` is coerced because a bare `0 &&` renders the zero. */}
      {!!credentials?.length && (
        <p className="text-xs text-muted-foreground" data-testid="codes-remaining">
          {codesRemaining === 0
            ? 'No recovery codes left. Generate a new set before you lose access to your key.'
            : `${String(codesRemaining)} recovery code${codesRemaining === 1 ? '' : 's'} remaining.`}
        </p>
      )}

      <div className="flex gap-2">
        <Button
          data-testid="enroll-security-key"
          onClick={() => { setError(undefined); setName(''); setAdding('security_key'); }}
        >
          <KeyRound className="mr-2 h-4 w-4" />
          Add a security key
        </Button>
        <Button
          variant="outline"
          data-testid="enroll-passkey"
          onClick={() => { setError(undefined); setName(''); setAdding('passkey'); }}
        >
          Add a passkey
        </Button>
      </div>

      {/* The name is asked for *before* the browser prompt, not beside the buttons where it read
          as an unrelated field. Once the ceremony starts the authenticator owns the screen, so
          anything we need from the user has to be collected first. */}
      <Dialog
        open={adding !== undefined}
        onOpenChange={(open) => { if (!open && !enroll.isPending) setAdding(undefined); }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {adding === 'passkey' ? 'Add a passkey' : 'Add a security key'}
            </DialogTitle>
            <DialogDescription>
              {adding === 'passkey'
                ? 'A passkey signs you in on its own — no password. Your device will ask for your PIN, fingerprint, or face.'
                : 'A security key is a second step after your password. You will be asked to touch it.'}
            </DialogDescription>
          </DialogHeader>

          {error && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="key-name">Name</Label>
            <Input
              id="key-name"
              data-testid="key-name"
              placeholder={adding === 'passkey' ? 'This laptop' : 'YubiKey on my keyring'}
              autoFocus
              value={name}
              disabled={enroll.isPending}
              onChange={(e) => { setName(e.target.value); }}
            />
            <p className="text-xs text-muted-foreground">
              Optional. It only helps you tell your keys apart later — you can rename it any time.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              disabled={enroll.isPending}
              onClick={() => { setAdding(undefined); }}
            >
              Cancel
            </Button>
            <Button
              data-testid="confirm-enroll"
              disabled={enroll.isPending}
              onClick={() => { if (adding) enroll.mutate(adding); }}
            >
              {enroll.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
              {enroll.isPending ? 'Waiting for your device…' : 'Continue'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
}
